<!--
  Created 10.10.2023
  Program component for the proposal show page.
-->
<template>
  <section
    class="relative bg-gray-100 print:!bg-transparent"
    :class="{ 'hidden': !contentEditable && !proposal.data.sections.program.visible }">
    <!-- Adjustment icon/box -->
    <SectionOptions
      v-if="contentEditable"
      class="z-20"
      section="program" />
    <!-- disabled cursor overlay if section is hidden -->
    <div
      v-if="!store.printMode"
      class="hidden"
      :class="{ 'absolute left-0 top-0 z-10 !block size-full cursor-not-allowed' : !proposal.data.sections.program.visible }" />
    <div
      class="container mx-auto px-6 py-12 pb-16 md:py-24 md:pb-28 print:!py-8"
      :class="{ 'pointer-events-none opacity-50' : !proposal.data.sections.program.visible }">
      <div class="flex flex-col lg:flex-row">
        <!-- Program left/top section -->
        <div
          v-if="!store.printMode"
          class="lg:basis-1/3">
          <TextEditableTextarea
            v-if="contentEditable"
            id="section-program-heading"
            v-model="proposal.data.sections.program.heading"
            background-style="gray"
            class="lg:mr-2 xl:mr-8 2xl:mr-24"
            :text-area-class="'bg-transparent whitespace-pre-wrap md:leading-snug lg:leading-tight '
              + 'text-2xl md:text-3xl lg:text-4xl font-medium text-gray-800 '
              + 'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
            :placeholder="'Itinerary heading..'"
            :show-buttons="false"
            :saved-changes="savedChangesProposal"
            @edited="store.unsavedChanges.changes.proposal = true;" />
          <h1
            v-else
            class="text-2xl font-medium text-gray-800 md:text-3xl lg:text-4xl">
            {{ proposal.data?.sections?.program?.heading ?? 'Programoversikt' }}
          </h1>
          <TextEditableTextarea
            v-if="contentEditable"
            id="section-program-ingress"
            v-model="proposal.data.sections.program.ingress"
            background-style="gray"
            class="mt-4 lg:mr-2 lg:mt-8 xl:mr-8 2xl:mr-24"
            :text-area-class="'bg-transparent whitespace-pre-wrap md:leading-snug lg:leading-tight text-gray-600 text-md lg:text-lg ' +
              'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
            :placeholder="'Fill in itinerary ingress..'"
            :show-buttons="false"
            :saved-changes="savedChangesProposal"
            @edited="store.unsavedChanges.changes.proposal = true;" />
          <div
            v-else-if="proposal.data?.sections?.program?.ingress"
            class="mt-4 whitespace-pre-wrap text-gray-600 md:leading-snug lg:mr-8 lg:mt-8 lg:space-y-2 lg:text-lg lg:leading-tight print:!hidden">
            {{ proposal.data.sections.program.ingress }}
          </div>
          <button
            v-if="contentEditable"
            type="button"
            class="text-md mt-12 inline-flex items-center rounded-full border border-gray-300 bg-white py-2 pl-4 pr-8 font-semibold
                text-gray-600 opacity-50 shadow-md shadow-gray-300 hover:opacity-100
                focus:outline-none focus:ring-2 focus:ring-purple-800 focus:ring-offset-2
                lg:py-3 lg:pl-4 lg:pr-8 lg:text-lg"
            @click="showMapModal = true">
            <PlusSmallIcon
              class="size-8"
              aria-hidden="true" /> <span class="ml-1">Add to proposal</span>
          </button>
        </div>
        <!-- Program right/bottom section -->
        <div class="relative flex flex-col max-lg:mt-8 lg:basis-2/3">
          <!-- Intinerary list-->
          <div
            v-if="itinerary.length > 0"
            class="grid grid-cols-1">
            <Accordion
              v-for="(day, index) in itinerary"
              :key="'Accordion-day-' + day.id"
              class="print:first-of-type:break-before-page"
              :hide-button-icons="store.printMode"
              :state="day.active"
              :button-class="'py-4 lg:p-4 hover:bg-aqua-50' + (day.active ? ' border-transparent' : '') + 'print:!pb-0 print:!pt-8'"
              @toggle="(state) => toggleAccordions(state, day)">
              <template
                v-if="store.printMode && index === 0"
                #preTitle>
                <h1 class="mb-4 mt-8 text-2xl font-medium text-gray-800 md:text-3xl lg:text-4xl">
                  {{ proposal.data?.sections?.program?.heading ?? 'Programoversikt' }}
                </h1>
              </template>
              <template #title>
                <h2 class="text-left text-xl font-medium text-gray-800 md:text-2xl lg:text-2xl">
                  {{ day.title }}
                </h2>
              </template>
              <template
                #content="{ state }">
                <div :class="(day.active && index !== itinerary.length - 1) ? 'border-b border-gray-200 print:!border-transparent' : ''">
                  <div v-if="day.list !== undefined && day.list?.length > 0 && state">
                    <div
                      v-for="listItem in day.list.filter((item) => item.time !== undefined
                        && entities[item.entityType][item.entityIndex] !== undefined)"
                      :key="'AccordionContent-' + day.id + '--' + listItem?.id"
                      class="group bg-gray-100 last:pb-4 print:!bg-transparent"
                      :class="[
                        { 'hidden': !listItem.visibleInProgram && !contentEditable },
                        { 'cursor-not-allowed': !listItem.visibleInProgram && contentEditable }
                      ]">
                      <div class="relative space-y-2 py-4 md:w-11/12 lg:pl-4">
                        <div class="ml-0.5 flex items-center justify-between space-x-4">
                          <!-- entity time, start and end -->
                          <div
                            class="flex items-center"
                            :class="{ 'pointer-events-none opacity-25': !listItem.visibleInProgram }">
                            <!-- Start date -->
                            <span class="text-sm font-bold text-gray-700">kl. {{ getDateTimeInDestinationTimezone(
                              entities[listItem.entityType][listItem.entityIndex].start, entities[listItem.entityType][listItem.entityIndex].destination.timezone, 'HH:mm'
                            ) }}</span>
                            <!-- End date -->
                            <template v-if="entities[listItem.entityType][listItem.entityIndex].end">
                              <span
                                v-if="isSameDay(entities[listItem.entityType][listItem.entityIndex].start, entities[listItem.entityType][listItem.entityIndex].end)"
                                class="inline-block text-sm font-bold text-gray-700">
                                &nbsp;&ndash;&nbsp;{{ getDateTimeInDestinationTimezone(
                                  entities[listItem.entityType][listItem.entityIndex].end, entities[listItem.entityType][listItem.entityIndex].destination.timezone, 'HH:mm'
                                ) }}
                              </span>
                              <span
                                v-else
                                class="inline-block text-sm font-bold text-gray-700">
                                &nbsp;&ndash;&nbsp;{{ getDateTimeInDestinationTimezone(
                                  entities[listItem.entityType][listItem.entityIndex].end, entities[listItem.entityType][listItem.entityIndex].destination.timezone, 'ddd DD/MM [kl.] HH:mm'
                                ) }}
                              </span>
                            </template>
                            <!-- Entity type -->
                            <span
                              v-if="proposal.destinations.length > 1 && entities[listItem.entityType][listItem.entityIndex]?.place_entity?.parent?.name"
                              class="ml-1 inline-block cursor-default text-sm font-bold text-gray-700">i {{ entities[listItem.entityType][listItem.entityIndex]?.place_entity?.parent?.name }}</span>
                            <span
                              v-if="contentEditable"
                              class="ml-1">&bull;</span>
                            <span
                              v-if="contentEditable"
                              class="ml-1 text-sm font-bold capitalize text-cyan-700">{{ entities[listItem.entityType][listItem.entityIndex].type }}</span>
                            <!-- If alternative text -->
                            <span
                              v-if="listItem.entity.alternative"
                              class="ml-1">&bull;</span>
                            <span
                              v-if="listItem.entity.alternative"
                              class="ml-1 text-sm font-bold text-cyan-700">Alternativ mulighet</span>
                          </div>
                          <!-- Itinerary item options -->
                          <div
                            v-if="contentEditable"
                            class="pointer-events-auto flex flex-row items-center space-x-2"
                            :class="{ 'cursor-auto': !listItem.visibleInProgram }">
                            <Tooltip
                              :disabled="!contentEditable"
                              class="relative z-20"
                              align="right"
                              :class="{'pointer-events-none':!proposal.data.sections.program.visible}"
                              content-class="ml-4 mt-2 bg-gray-900 text-gray-100 w-56 p-2 rounded opacity-95"
                              trigger-class="flex">
                              <template #trigger>
                                <ShortToggle
                                  color-enabled="bg-neutral-500"
                                  :enabled="listItem.visibleInProgram"
                                  @update:enabled="(enabledState) => toggleItineraryItemVisibility(listItem, enabledState)" />
                              </template>
                              <template #content>
                                <p class="text-xs">
                                  Click to toggle this item's visibility in the program. It will remain visible elsewhere in the proposal.
                                </p>
                              </template>
                            </Tooltip>
                            <Tooltip
                              :disabled="!contentEditable"
                              class="relative z-20"
                              align="right"
                              :class="{'pointer-events-none':!proposal.data.sections.program.visible}"
                              content-class="ml-4 mt-2 bg-gray-900 text-gray-100 w-56 p-2 rounded opacity-95">
                              <template #trigger>
                                <AdjustmentsHorizontalIcon
                                  class="size-7 rounded-md text-gray-400 drop-shadow-sm hover:text-gray-500"
                                  aria-hidden="true"
                                  @click="openItineraryEntitySettingsModal(listItem)" />
                              </template>
                              <template #content>
                                <p class="text-xs">
                                  Click to edit itinerary entity settings.
                                </p>
                              </template>
                            </Tooltip>
                          </div>
                        </div>
                        <!-- Entity info -->
                        <div
                          class="relative flex flex-row lg:flex-none"
                          :class="{ 'pointer-events-none opacity-25': !listItem.visibleInProgram }">
                          <!-- Media -->
                          <div
                            class="mr-3 mt-0 aspect-[5/4] h-[96px] w-[120px]">
                            <Tooltip
                              v-if="entities[listItem.entityType][listItem.entityIndex].data.image?.mime_type"
                              class="relative z-10"
                              align="left"
                              content-class="mt-2 bg-gray-900 text-gray-100 w-max p-2 rounded opacity-95"
                              trigger-class="relative aspect-[5/4] h-full">
                              <template #trigger>
                                <Media
                                  :media-items="[entities[listItem.entityType][listItem.entityIndex].data.image]"
                                  class=""
                                  hide-fig-caption
                                  image-classes="rounded border-2 border-gray-300"
                                  @click="openItineraryEntityModal(listItem)" />
                              </template>
                              <template #content>
                                <p class="text-xs">
                                  Click to see more
                                </p>
                              </template>
                            </Tooltip>
                            <Component
                              :is="entityTypesIconMapping[listItem.entity.type]"
                              v-else
                              class="aspect-[5/4] h-[96px] w-[120px] rounded border-2 border-gray-300 bg-gray-200 p-0.5 text-gray-400" />
                          </div>
                          <!-- Texts -->
                          <div class="w-full">
                            <TextEditableTextarea
                              v-if="contentEditable"
                              :id="`entity-${listItem.entity.id}-name`"
                              v-model="entities[listItem.entityType][listItem.entityIndex].texts[currentLanguage].name"
                              class="w-full"
                              background-style="gray"
                              :text-area-class="'bg-transparent whitespace-pre-wrap text-lg font-bold text-gray-800 ' +
                                'placeholder:italic placeholder:text-gray-600 placeholder:opacity-50 cursor-text'"
                              :placeholder="'entity name here..'"
                              :show-buttons="false"
                              :saved-changes="savedChangesItinerary"
                              @edited="editEntityInProposal(listItem)" />
                            <h3
                              v-else
                              class="text-lg font-bold text-gray-800">
                              {{ entities[listItem.entityType][listItem.entityIndex].texts[currentLanguage].name }}
                            </h3>
                            <TextEditableTextarea
                              v-if="contentEditable"
                              :id="`entity-${listItem.entity.id}-proposal-program-text`"
                              v-model="entities[listItem.entityType][listItem.entityIndex].texts[currentLanguage].proposalProgramText"
                              :tab-rows="1"
                              class="w-full"
                              background-style="gray"
                              :text-area-class="'bg-transparent whitespace-pre-wrap whitespace-pre-wrap leading-snug text-gray-800 ' +
                                'placeholder:italic placeholder:text-gray-600 placeholder:opacity-50 cursor-text'"
                              :placeholder="'entity proposal program text here..'"
                              :show-buttons="false"
                              :saved-changes="savedChangesItinerary"
                              @edited="editEntityInProposal(listItem)" />
                            <template v-else>
                              <p
                                v-if="entities[listItem.entityType][listItem.entityIndex].texts[currentLanguage].proposalProgramText"
                                class="whitespace-pre-wrap leading-snug text-gray-800">
                                <StringLimiter
                                  v-if="!store.printMode"
                                  :string="entities[listItem.entityType][listItem.entityIndex].texts[currentLanguage].proposalProgramText"
                                  :length="250"
                                  is-simple />
                                <template v-else>
                                  {{ entities[listItem.entityType][listItem.entityIndex].texts[currentLanguage].proposalProgramText }}
                                </template>
                              </p>
                            </template>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else
                    class="border-b border-gray-200 bg-gray-100 print:border-transparent print:bg-transparent">
                    <p class="pb-6 pl-4 text-sm italic">
                      {{ contentEditable ? 'There is no program registered for this day.' : 'Det er ikke noe i programmet for denne dagen.' }}
                    </p>
                  </div>
                </div>
              </template>
            </Accordion>
          </div>
          <!-- Hide/Show all rows -->
          <button
            class="mt-6 block w-fit rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm
              hover:border-purple-900 hover:bg-purple-800 hover:text-white focus:outline-none
              focus:ring-2 focus:ring-purple-800 focus:ring-offset-2 print:hidden"
            @click="() => {
              toggleAccordions(itinerary[itinerary?.length-1]?.active, null, true);
              $nextTick(() => {
                $el.scrollIntoView({ behavior: 'smooth' });
              });
            }">
            {{ itinerary[itinerary?.length-1]?.active ? 'Lukk' : 'Åpne' }} alle dager
          </button>
        </div>
      </div>
    </div>

    <!-- Add entity Map modal -->
    <SimpleModal
      v-if="contentEditable"
      :show="showMapModal"
      :box-class="[
        'w-11/12',
      ]"
      :outside-click="true"
      @update:show="(value) => showMapModal = value">
      <template #content>
        <header class="border-b border-gray-200 bg-gray-100 p-6">
          <div class="flex items-center justify-between pr-8">
            <div class="flex flex-col">
              <span>Select, add or edit your entities from destination <strong>{{ proposal.destinations[0].name }}</strong> in <strong>{{ proposal.destinations[0].area }}</strong></span>
              <span
                v-if="proposal.destinations.length > 1"
                class="text-sm">You also have entities available from:
                <span
                  v-for="(destination, index) in proposal.destinations.slice(1)"
                  :key="index">
                  <strong>{{ destination.name }}</strong>
                  <template v-if="index !== proposal.destinations.slice(1).length - 1">, </template>
                </span>
              </span>
            </div>
            <div class="space-x-2">
              <button
                type="button"
                class="inline-flex items-center rounded-md border border-black py-1 pl-2 pr-4 text-sm font-medium
                  text-black shadow-sm hover:border-purple-700 hover:bg-purple-700 hover:text-white focus:outline-none
                  focus:ring-2 focus:ring-purple-800 focus:ring-offset-2"
                @click="toggleDestinationsModal">
                <PlusSmallIcon
                  class="size-6"
                  aria-hidden="true" /> <span class="ml-1">Add destination</span>
              </button>
              <button
                type="button"
                class="inline-flex items-center rounded-md border border-black py-1 pl-2 pr-4 text-sm font-medium
                  text-black shadow-sm hover:border-purple-700 hover:bg-purple-700 hover:text-white focus:outline-none
                  focus:ring-2 focus:ring-purple-800 focus:ring-offset-2"
                @click="toggleModal">
                <PlusSmallIcon
                  class="size-6"
                  aria-hidden="true" /> <span class="ml-1">Add more entities to <strong>{{ proposal.destinations[0].name }}</strong></span>
              </button>
            </div>
          </div>
        </header>
        <DestinationMapEntites
          :destination="proposal.destinations[0]"
          :destinations="proposal.destinations"
          v-bind="$attrs"
          @selected-entity="(entity) => { selectedEntity = entity }" />
        <footer class="flex items-center justify-end space-x-4 border-t border-gray-200 bg-gray-100 p-4">
          <div class="flex flex-row items-center space-x-2">
            <label
              for="set-start-time"
              class="whitespace-nowrap text-sm font-medium"
              :class="!selectedEntityDate ? 'text-red-600 font-bold' : ''">Set start time:</label>
            <Datepicker
              id="set-start-time"
              v-model="selectedEntityDate"
              class="w-56"
              menu-class-name="p-2"
              required
              :class="!selectedEntityDate ? 'text-red-600' : ''"
              :state="!selectedEntityDate ? false : ''"
              :week-numbers="{ type: 'local' }"
              six-weeks="fair"
              :minutes-increment="15"
              :start-time="{
                hours: 12,
                minutes: 0,
              }"
              partial-flow
              text-input
              :config="{ keepActionRow: true }"
              time-picker-inline
              :timezone="proposal.data.destination.timezone"
              :locale="currentLocale"
              :format="'dd.MM.yyyy HH:mm'"
              :allowed-dates="allowedDates"
              :start-date="allowedDates[0]" />
          </div>

          <div class="flex flex-row items-center space-x-2">
            <label
              for="set-end-time"
              class="whitespace-nowrap text-sm font-medium">Optional end time:</label>
            <Datepicker
              id="set-end-time"
              v-model="selectedEntityDateEnd"
              class="w-56"
              menu-class-name="p-2"
              :class="selectedEntityDateEnd && selectedEntityDate && selectedEntityDateEnd.getTime() < selectedEntityDate.getTime() ? 'text-red-600' : ''"
              :state="selectedEntityDateEnd && selectedEntityDate && selectedEntityDateEnd.getTime() < selectedEntityDate.getTime() ? false : ''"
              :week-numbers="{ type: 'local' }"
              six-weeks="fair"
              :minutes-increment="15"
              :start-time="{
                hours: 13,
                minutes: 0,
              }"
              partial-flow
              text-input
              :config="{ keepActionRow: true }"
              time-picker-inline
              :timezone="proposal.data.destination.timezone"
              :locale="currentLocale"
              :format="'dd.MM.yyyy HH:mm'"
              :allowed-dates="allowedDates"
              :start-date="allowedDates[0]" />
          </div>

          <button
            type="button"
            class="inline-flex shrink-0 items-center rounded-full border-2 border-transparent bg-purple-700 px-4 py-2 text-sm font-medium
                text-white shadow-sm hover:border-purple-900 hover:bg-purple-800 hover:text-white focus:outline-none
                focus:ring-2 focus:ring-purple-800 focus:ring-offset-2"
            :class="{ 'cursor-not-allowed !border-gray-500 !bg-gray-500 opacity-50 focus:ring-transparent': !(selectedEntity && selectedEntityDate)
              || (selectedEntityDateEnd && selectedEntityDate && selectedEntityDateEnd.getTime() < selectedEntityDate.getTime())
            }"
            @click="addEntityToProposal(proposal.destinations[0], selectedEntity, selectedEntityDate, selectedEntityDateEnd)">
            Add selected entity to proposal
          </button>
        </footer>
      </template>
    </SimpleModal>

    <!-- Add destination modal -->
    <SimpleModal
      v-if="contentEditable"
      :show="showDestinationsModal"
      :box-class="[
        'w-3/5',
      ]"
      :outside-click="true"
      @update:show="(value) => showDestinationsModal = value">
      <template #content>
        <header class="border-b border-gray-200 bg-gray-100 p-6">
          Add more destinations to this proposal
        </header>
        <main class="flex h-fit max-h-[calc(100vh-200px)] min-h-[600px] flex-row">
          <div class="flex basis-1/2 flex-col space-y-4 overflow-y-scroll p-8">
            <!-- New row -->
            <div class="space-y-2 text-sm">
              <p>Here you can add more destinations to this proposal.</p>
              <p>
                Destinations cannot be removed from the proposal once added,
                due to any connected entities which may have been added.
              </p>
            </div>
            <!-- New row -->
            <div class="!mt-4 flex flex-col space-y-1">
              <h3 class="text-lg font-medium">
                Destinations currently selected
              </h3>
              <p class="text-xs italic text-gray-600">
                Those already in proposal will appear yellow.
              </p>
            </div>
            <!-- New row -->
            <div class="flex flex-col space-y-1">
              <div
                v-if="newDestinations && newDestinations.length > 0"
                class="space-x-2">
                <span
                  v-for="(item, index) in newDestinations"
                  :key="'selectedItems-' + item.id"
                  :title="item.readOnly ? 'This destination is already added and cannot be removed' : 'You can remove this destination by clicking the X'"
                  class="inline-flex items-center rounded-full bg-indigo-100 py-1 pl-2 pr-0.5 text-xs font-medium text-indigo-700"
                  :class="{'cursor-default !bg-amber-100 pr-2 !text-amber-700' : item.readOnly}">
                  <slot
                    name="label"
                    :item="item">
                    {{ item.name }}
                  </slot>
                  <button
                    v-if="!item.readOnly"
                    type="button"
                    class="ml-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full text-indigo-400 hover:bg-indigo-200
              hover:text-indigo-500 focus:bg-indigo-500 focus:text-white focus:outline-none"
                    @click="newDestinationComponent.deselectItem(item, index)">
                    <span class="sr-only">Remove {{ item.name }} from selection</span>
                    <svg
                      class="size-2"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 8 8">
                      <path
                        stroke-linecap="round"
                        stroke-width="1.5"
                        d="M1 1l6 6m0-6L1 7" />
                    </svg>
                  </button>
                </span>
              </div>
            </div>
          </div>
          <div class="basis-1/2 overflow-y-scroll bg-gray-200 p-8">
            <!-- Program text -->
            <div class="mt-4 rounded-md bg-white p-4">
              <h3 class="text-md text-lg font-medium">
                Add destinations to proposal
              </h3>
              <p class="text-sm text-gray-600">
                If you can't find the destination you're looking for, you need to add it to your teams'
                <Link
                  class="link underline"
                  :href="route('destinations')">
                  {{ 'destinations' }}
                </Link>
                first.
              </p>
            </div>
            <ComboboxWithSecondaryText
              ref="newDestinationComponent"
              v-model="newDestinations"
              class="mt-2"
              has-multiple
              :show-list-above-input="false"
              :disabled="destinationsAvailable.length < 1"
              :placeholder="destinationsAvailable.length > 0 ? 'Search for or select destinations' : 'There are no Destinations, please add some'"
              :items="destinationsAvailable" />
          </div>
        </main>
        <footer class="flex items-center justify-between space-x-2 border-t border-gray-200 bg-gray-100 p-4">
          <p class="shrink text-sm">
            Changes will only be applied after you hit save
          </p>
          <button
            type="button"
            class="inline-flex shrink-0 items-center rounded-full border-2 border-transparent bg-purple-700 px-4 py-2 text-sm font-medium
                text-white shadow-sm hover:border-purple-900 hover:bg-purple-800 hover:text-white focus:outline-none
                focus:ring-2 focus:ring-purple-800 focus:ring-offset-2"
            @click="saveNewDestinations">
            Save changes
          </button>
        </footer>
      </template>
    </SimpleModal>

    <!-- Itinerary Entity Settings Modal -->
    <SimpleModal
      v-if="contentEditable && selectedItineraryEntity"
      :show="showItineraryEntitySettingsModal"
      :box-class="[
        'w-10/12',
      ]"
      :outside-click="true"
      @update:show="(value) => showItineraryEntitySettingsModal = value">
      <template #content>
        <header class="border-b border-gray-200 bg-gray-100 p-6">
          Change settings of itinerary entity
          <strong>{{ entities[selectedItineraryEntity.entityType][selectedItineraryEntity.entityIndex].texts[currentLanguage].name }}</strong>
          currently set to <strong>{{ getDateTimeInDestinationTimezone(
            entities[selectedItineraryEntity.entityType][selectedItineraryEntity.entityIndex].start,
            entities[selectedItineraryEntity.entityType][selectedItineraryEntity.entityIndex].destination.timezone,
            'ddd DD/MM [kl.] HH:mm'
          ) }}</strong>
        </header>
        <main class="flex h-[calc(100vh-200px)] flex-row">
          <div class="flex basis-1/2 flex-col space-y-4 overflow-y-scroll p-8">
            <!-- New row -->
            <div class="space-y-2 text-sm">
              <p>
                Here you can change settings for the current itinerary entity, as shown on the right-hand side.
                You can also fetch original entity content, or save your current version as a new entity content version.
              </p>
              <p>Note that changes will only take effect clicking the save changes button.</p>
            </div>
            <!-- New row -->
            <div class="!mt-6 flex flex-col space-y-1">
              <h3 class="text-lg font-medium">
                Proposal entity settings
              </h3>
            </div>
            <!-- New row -->
            <div class="flex flex-row space-x-4">
              <div class="flex flex-col space-y-1">
                <label
                  for="change-start-time"
                  class="text-sm font-medium">Change start time:</label>
                <Datepicker
                  v-model="selectedItineraryEntity.entity.start"
                  name="change-start-time"
                  position="left"
                  class="w-52"
                  menu-class-name="p-2"
                  required
                  :class="!selectedItineraryEntity.entity.start ? 'text-red-600' : ''"
                  :state="!selectedItineraryEntity.entity.start ? false : ''"
                  :week-numbers="{ type: 'local' }"
                  six-weeks="fair"
                  :minutes-increment="15"
                  :start-time="{
                    hours: 12,
                    minutes: 0,
                  }"
                  partial-flow
                  text-input
                  :config="{ keepActionRow: true }"
                  time-picker-inline
                  :clearable="false"
                  :timezone="proposal.data.destination.timezone"
                  :locale="currentLocale"
                  :format="'dd.MM.yyyy HH:mm'"
                  :allowed-dates="allowedDates"
                  :start-date="allowedDates[0]" />
              </div>
              <div class="flex flex-col space-y-1">
                <label
                  for="set-end-time"
                  class="text-sm font-medium">Optional end time:</label>
                <Datepicker
                  v-model="selectedItineraryEntity.entity.end"
                  name="set-end-time"
                  position="right"
                  class="w-52"
                  :class="selectedItineraryEntity.entity.end
                    && selectedItineraryEntity.entity.start
                    && (new Date(selectedItineraryEntity.entity.end).getTime() < new Date(selectedItineraryEntity.entity.start).getTime())
                    ? 'text-red-600' : ''"
                  :state="selectedItineraryEntity.entity.end
                    && selectedItineraryEntity.entity.start
                    && (new Date(selectedItineraryEntity.entity.end).getTime() < new Date(selectedItineraryEntity.entity.start).getTime())
                    ? false : ''"
                  menu-class-name="p-2"
                  :week-numbers="{ type: 'local' }"
                  six-weeks="fair"
                  :minutes-increment="15"
                  :start-time="{
                    hours: 13,
                    minutes: 0,
                  }"
                  partial-flow
                  text-input
                  :config="{ keepActionRow: true }"
                  time-picker-inline
                  :timezone="proposal.data.destination.timezone"
                  :locale="currentLocale"
                  :format="'dd.MM.yyyy HH:mm'"
                  :allowed-dates="allowedDates"
                  :start-date="allowedDates[0]" />
              </div>
            </div>
            <!-- New row -->
            <div class="flex flex-col space-y-1">
              <label
                for="set-as-alternative"
                class="text-sm font-medium">Is an alternative:</label>
              <ShortToggle
                name="set-as-alternative"
                :enabled="selectedItineraryEntity.entity.alternative === 1"
                @update:enabled="(value) => {
                  selectedItineraryEntity.entity.alternative = (value ? 1 : 0)
                  selectedItineraryEntity.entity.data.visibleInProgram = !value
                }" />
            </div>
            <!-- New row -->
            <div class="!mt-8 flex flex-col space-y-1">
              <h3 class="text-lg font-medium">
                Original entity version handling
              </h3>
            </div>
            <!-- New row -->
            <div class="flex flex-col space-y-1">
              <label
                for="save-as-new-version"
                class="text-sm font-medium">Select another version or save proposal version to original entity:</label>
              <SelectMenuCustomWithAvatar
                v-model="selectedItineraryEntityElement"
                :elements="selectedItineraryEntityOriginalContentVersions"
                :selected-element="selectedItineraryEntityElement"
                title-alternative
                enable-create-options
                create-option-text="Save current proposal version as a new entity version"
                class="z-50 grow"
                @create-new-option="saveItineraryEntityContentVersionToOriginalEntity" />
            </div>
            <!-- New row -->
            <div class="flex flex-col space-y-1">
              <button
                v-if="!selectedItineraryEntityElement?.isProposalVersion"
                class="w-fit rounded-lg border-2 border-transparent bg-yellow-700 px-4 py-2 text-xs font-medium
                text-white shadow-sm hover:border-yellow-900 hover:bg-yellow-800 hover:text-white focus:outline-none
                focus:ring-2 focus:ring-yellow-800 focus:ring-offset-2"
                @click="overwriteOriginalEntityVersionWithProposalVersion">
                Overwrite selected entity version with proposal version
              </button>
              <p
                v-else
                class="text-sm italic">
                You can overwrite an original entity version, with the current proposal version, after selecting it in the dropdown above.
              </p>
            </div>
          </div>
          <div class="basis-1/2 overflow-y-scroll bg-gray-200 p-8">
            <EntityCard
              :entity="selectedItineraryEntityElement"
              :entities-type="entityTypesPluralMapping[selectedItineraryEntityElement.type]"
              is-compact
              is-vertical />
            <!-- Program text -->
            <div class="mt-4 bg-white p-4">
              <h3 class="text-md font-medium">
                Proposal program text
              </h3>
              <p
                v-if="selectedItineraryEntityElement.texts[currentLanguage].proposalProgramText"
                class="!mt-2 whitespace-pre-wrap text-sm font-normal leading-snug text-gray-800">
                <StringLimiter
                  :string="selectedItineraryEntityElement.texts[currentLanguage].proposalProgramText"
                  :length="250"
                  is-simple />
              </p>
              <p
                v-else
                class="!mt-2 whitespace-pre-wrap text-sm font-normal italic leading-snug text-gray-500">
                No program text has been added to this entity.
              </p>
            </div>
          </div>
        </main>
        <footer class="flex items-center justify-between space-x-2 border-t border-gray-200 bg-gray-100 p-4">
          <p class="shrink text-sm">
            Changes will only apply to this specific itinerary item, and not the original entity itself.
          </p>
          <button
            class="size-fit rounded-lg border-2 border-transparent bg-red-700/50 px-2 py-1 text-xs font-medium text-white
                  shadow-sm hover:border-red-800 hover:bg-red-700 hover:text-white focus:outline-none focus:ring-2
                  focus:ring-red-800 focus:ring-offset-2"
            @click="removeItineraryItem">
            Remove from proposal
          </button>
          <button
            type="button"
            class="inline-flex shrink-0 items-center rounded-full border-2 border-transparent bg-purple-700 px-4 py-2 text-sm font-medium
                text-white shadow-sm hover:border-purple-900 hover:bg-purple-800 hover:text-white focus:outline-none
                focus:ring-2 focus:ring-purple-800 focus:ring-offset-2"
            :class="{ 'cursor-not-allowed !border-gray-500 !bg-gray-500 opacity-50 focus:ring-transparent': !(selectedItineraryEntity && selectedItineraryEntity.entity.start)
              || (selectedItineraryEntity.entity.end && selectedItineraryEntity.entity.start
                && (new Date(selectedItineraryEntity.entity.end).getTime() < new Date(selectedItineraryEntity.entity.start).getTime()))
            }"
            @click="selectedItineraryEntity && selectedItineraryEntity.entity.start &&
              (!selectedItineraryEntity.entity.end || new Date(selectedItineraryEntity.entity.end).getTime() >= new Date(selectedItineraryEntity.entity.start).getTime())
              ? saveItineraryEntityChanges({
                ...toRaw(selectedItineraryEntity),
                entity: {
                  ...toRaw(selectedItineraryEntity.entity),
                  ...toRaw(selectedItineraryEntityElement),
                  id: selectedItineraryEntity.entity.id,
                  start: selectedItineraryEntity.entity.start,
                  end: selectedItineraryEntity.entity.end,
                  alternative: selectedItineraryEntity.entity.alternative,
                }
              }) : null">
            Save changes
          </button>
        </footer>
      </template>
    </SimpleModal>

    <!-- Single entity info modal -->
    <SimpleModal
      box-class="w-11/12 md:w-4/5 lg:w-10/12 max-w-[1000px] max-h-[80vh] overflow-y-auto"
      :show="showItineraryEntityModal && showItineraryEntitySelected"
      @update:show="(value) => showItineraryEntityModal = value">
      <template #content>
        <EntityCard
          v-if="showItineraryEntityModal"
          ref="myItineraryEntitySelectedCard"
          class="rounded-xl"
          :entity="showItineraryEntitySelected.entity"
          :content-editable="contentEditable"
          :entities-type="showItineraryEntitySelected.entityType"
          :entity-index="showItineraryEntitySelected.entityIndex"
          hide-entity-settings-button
          is-rounded
          has-no-string-limit
          has-white-background
          grid-column-class="grid-cols-1" />
      </template>
    </SimpleModal>
  </section>
</template>
<script setup>
/* external */
import {
  UserIcon, AdjustmentsHorizontalIcon,
} from '@heroicons/vue/24/outline';
import {
  PlusSmallIcon, UserGroupIcon, UserIcon as UserIconSolid,
} from '@heroicons/vue/24/solid';
import {
  computed, onMounted, provide, ref, toRaw, useAttrs, watch,
} from 'vue';
import Cookies from 'js-cookie';
import { notify } from 'notiwind';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

/* local */
import { useProposalStore } from '@/Stores/Proposal';
import {
  currentDateLocalized, formatToMySQLDateTime, uniqueId,
  isSameDay, currentLocale, getDateTimeInDestinationTimezone,
} from '@/utils';
import StringLimiter from '@/Components/StringLimiter.vue';
import Accordion from '@/Components/Accordion.vue';
import SimpleModal from '@/Components/Modals/SimpleModal.vue';
import DestinationMapEntites from '@/Pages/Destination/Components/DestinationMapEntites.vue';
import TextEditableTextarea from '@/Components/Forms/TextEditableTextarea.vue';
import EntityCard from '@/Pages/Proposal/Components/EntityCard.vue';
import ShortToggle from '@/Components/Forms/ShortToggle.vue';
import Tooltip from '@/Components/Tooltip.vue';
import SelectMenuCustomWithAvatar from '@/Components/Selectors/SelectMenuCustomWithAvatar.vue';
import Media from '@/Pages/Proposal/Components/Media.vue';
import SectionOptions from '@/Pages/Proposal/Components/SectionOptions.vue';
import ComboboxWithSecondaryText from '@/Components/Forms/ComboboxWithSecondaryText.vue';
import { Link } from '@inertiajs/vue3';

/* *********************************************************************
 * Component declarations
 * ****************************************************************** */

const attrs = useAttrs();

const props = defineProps({
  contentEditable: {
    type: Boolean,
    required: false,
    default: false,
  },
  availableDestinations: {
    type: Array,
    required: false,
    default: () => [],
  },
});

const currentLanguage = ref(Cookies.get('currentLanguage') ?? 'no');
const currentUser = attrs.user;

const store = useProposalStore();
const proposal = computed(() => store.proposal);
const itinerary = computed(() => store.itinerary);
const entities = computed(() => store.entities);
const openEntityItinerarySettings = computed(() => store.openEntityItinerarySettings);
const entityTypesPluralMapping = computed(() => store.entityTypesPluralMapping);
const entityTypesIconMapping = computed(() => store.entityTypesIconMapping);
const allowedDates = computed(() => store.days.map((day) => day.systemDate));
const savedChangesItinerary = computed(() => !store.unsavedChanges.changes.itinerary);
const savedChangesProposal = computed(() => !store.unsavedChanges.changes.proposal);

const showMapModal = ref(false);
const selectedEntity = ref(null);
const selectedEntityDate = ref(null);
const selectedEntityDateEnd = ref(null);

const showCreateEntityModal = ref(false);
provide('showCreateEntityModal', showCreateEntityModal);

const showItineraryEntityModal = ref(false);
const showItineraryEntitySelected = ref(null);

const showItineraryEntitySettingsModal = ref(false);
const selectedItineraryEntity = ref(null);
const selectedItineraryEntityOriginalContentVersions = ref([]);
const selectedItineraryEntityElement = ref();
const proposalItineraryEntityElement = ref();

const showDestinationsModal = ref(false);
const newDestinationComponent = ref(null);
const newDestinations = ref([]);

const destinationsAvailable = computed(() => props.availableDestinations.map((destination) => ({
  primaryText: destination.name,
  secondaryText: destination.parent?.name ?? destination.area ?? '',
  selected: proposal.value.destinations.some(
    (dest) => dest.id === destination.id,
  ),
  readOnly: proposal.value.destinations.some(
    (dest) => dest.id === destination.id,
  ),
  ...destination,
})).sort(
  (a, b) => (b.name - a.name),
));

/* *********************************************************************
 * Component functions
 * ****************************************************************** */

/**
 * Toggle the entity creation modal within map modal.
 */
function toggleModal() {
  showCreateEntityModal.value = !showCreateEntityModal.value;
}

/**
 * Toggle the destinations modal to add more destinations to proposal.
 */
function toggleDestinationsModal() {
  showDestinationsModal.value = !showDestinationsModal.value;
}

/**
 * Function to toggle accordion state.
 *
 * @param {boolean} state - The state of the accordion
 * @param {object} [current] - The current accordion object
 * @param {boolean} [toggleAll] - Toggle all accordions
 */
function toggleAccordions(state, current = null, toggleAll = false) {
  if (current) {
    itinerary.value.forEach((day) => {
      if (day.id === current.id) {
        day.active = !day.active;
      }
    });
  } else if (toggleAll) {
    itinerary.value.forEach((day) => {
      day.active = !state;
    });
  }
}

/**
 * Add a new destination to the proposal.
 */
function saveNewDestinations() {
  if (newDestinations.value.length > 0) {
    axios.post(route('api.proposal.destination.add', {
      proposal: proposal.value.id,
    }), {
      destinations: newDestinations.value.filter(((destination) => !destination.readOnly)),
    }).then(() => {
      notify({
        type: 'success',
        title: 'Saved',
        text: 'New destinations added to proposal. Reloading page..',
      }, 4000);
    }).catch((error) => {
      // eslint-disable-next-line no-console
      notify({
        type: 'danger',
        title: 'Something went wrong',
        text: 'An error occurred while saving the new destinations.',
      }, 4000);
      console.error(error);
    }).finally(() => {
      // rest in peace
    });
  }
}

/**
 * Add entity to proposal itinerary.
 * @param {object} destination - The destination the entity is from
 * @param {object} entity - The entity to add
 * @param {Date} date - The date to add the entity to
 * @param {Date} dateEnd - The optional end date of the entity
 */
function addEntityToProposal(destination, entity, date, dateEnd = null) {
  if (entity && date) {
    axios.post(route('api.proposal.itinerary.store', {
      proposal: proposal.value.id,
    }), {
      type: entity.type,
      place_id: entity.place_id,
      place_entity_id: entity.id,
      destination_id: destination.id,
      content_version_id: entity.dbSelectedVersion.id,
      data: entity.dbSelectedVersion.data,
      texts: entity.dbSelectedVersion.texts,
      start: formatToMySQLDateTime(date),
      end: dateEnd ? formatToMySQLDateTime(dateEnd) : '',
      alternative: false,
    }).then(() => {
      store.fetchEntities();
      notify({
        type: 'success',
        title: 'Saved',
        text: 'Entity added to proposal',
      }, 4000);
    }).catch((error) => {
      // eslint-disable-next-line no-console
      console.log(error);
    }).finally(() => {
      showMapModal.value = false;
      selectedEntity.value = null;
      selectedEntityDate.value = null;
      selectedEntityDateEnd.value = null;
    });
  }
}

/**
 * View itinerary entity.
 * @param {object} listItem - The itinerary entity to view
 */
function openItineraryEntityModal(listItem) {
  if (!listItem) {
    return;
  }
  showItineraryEntityModal.value = true;
  showItineraryEntitySelected.value = listItem;
}

/**
 * Edit entity in proposal itinerary.
 * @param {object} listItem - The list item to edit
 */
function editEntityInProposal(listItem) {
  store.unsavedChanges.changes.itinerary = true;

  axios.put(route('api.proposal.itinerary.update', {
    proposal: proposal.value.id, itinerary: listItem.entity.id,
  }), {
    ...entities.value[listItem.entityType][listItem.entityIndex],
  }).then(() => {
    store.unsavedChanges.changes.itinerary = false;
    // store.fetchEntities();
    notify({
      type: 'success',
      title: 'Saved',
      text: 'Entity updated!',
    }, 4000);
  }).catch((error) => {
    // eslint-disable-next-line no-console
    console.error(error);
  }).finally(() => {
    // rest in peace
  });
}

/**
 * Update itinerary entity.
 * @param {object} listItem - The itinerary entity to update
 */
function openItineraryEntitySettingsModal(listItem) {
  if (!listItem) {
    return;
  }
  showItineraryEntitySettingsModal.value = true;

  // we need to clone the object, otherwise the changes will be saved to the store in real time
  selectedItineraryEntity.value = toRaw({
    ...listItem,
    entity: {
      ...toRaw(listItem.entity),
    },
  });

  // set selected entity element to current entity
  selectedItineraryEntityElement.value = {
    ...selectedItineraryEntity.value.entity,
    id: uniqueId(),
    isProposalVersion: true,
    title: 'Proposal version',
    titleAlternative: 'Current proposal version',
    icon: UserIconSolid,
    parent_type: 'App\\Models\\PlaceEntity',
    parent_id: selectedItineraryEntity.value.entity.id,
  };

  // set a copy of the proposal entity version
  proposalItineraryEntityElement.value = selectedItineraryEntityElement.value;
  // set proposal versions to first entity in list
  selectedItineraryEntityOriginalContentVersions.value.push(selectedItineraryEntityElement.value);

  /**
   * Content version elements handling
   */
  // get original entity from proposal
  const originalEntity = computed(() => proposal.value.destinations[0].available_entities.find((element) => element.id === listItem.entity.place_entity_id));
  // if original entity exists add it to the list
  if (originalEntity.value) {
    // populate original entity content versions
    selectedItineraryEntityOriginalContentVersions.value = originalEntity.value?.content_versions.map((element) => {
      let icon;
      if (element.owner_type === 'App\\Models\\Team') {
        icon = UserGroupIcon;
      } else {
        icon = element.owner_id === currentUser.id ? UserIconSolid : UserIcon;
      }

      return {
        ...element,
        start: selectedItineraryEntityElement.value.start,
        end: selectedItineraryEntityElement.value.end,
        alternative: selectedItineraryEntityElement.value.alternative,
        highlighted: selectedItineraryEntityElement.value.highlighted,
        place_id: selectedItineraryEntityElement.value.place_id,
        destination_id: selectedItineraryEntityElement.value.destination_id,
        proposal_id: selectedItineraryEntityElement.value.proposal_id,
        content_version_id: element.id,
        place_entity_id: element.parent_id,
        type: selectedItineraryEntity.value.entity.type,
        destination: selectedItineraryEntity.value.entity.destination,
        titleAlternative: `${element.owner.name} - ${element.title}`,
        icon,
      };
    });

    // add proposal version to the beginning of the array
    selectedItineraryEntityOriginalContentVersions.value?.unshift(selectedItineraryEntityElement.value);
  }
}

/**
 * Save entity itinerary changes.
 * @param {object} listItem - Non-reactive copy of itinerary element to update reactive element on save
 */
function saveItineraryEntityChanges(listItem) {
  store.unsavedChanges.changes.itinerary = true;

  axios.put(route('api.proposal.itinerary.update', {
    proposal: proposal.value.id, itinerary: listItem.entity.id,
  }), {
    ...listItem.entity,
    start: formatToMySQLDateTime(listItem.entity.start),
    end: listItem.entity.end ? formatToMySQLDateTime(listItem.entity.end) : '',
  }).then(() => {
    store.unsavedChanges.changes.itinerary = false;
    showItineraryEntitySettingsModal.value = false;
    selectedItineraryEntity.value = null;
    notify({
      type: 'success',
      title: 'Saved',
      text: 'Entity updated!',
    }, 4000);
    store.fetchEntities();
  }).catch((error) => {
    // eslint-disable-next-line no-console
    console.error(error);
  }).finally(() => {
    // rest in peace
  });
}

/**
 * Save itinerary entity content version to original entity.
 */
function saveItineraryEntityContentVersionToOriginalEntity() {
  // create new element
  axios.post(route('api.content-version.store'), {
    parent_type: 'App\\Models\\PlaceEntity',
    parent_id: selectedItineraryEntity.value.entity.place_entity_id,
    owner_type: 'App\\Models\\User',
    owner_id: currentUser.id,
    standard: false,
    data: {
      ...selectedItineraryEntityElement.value.data,
    },
    texts: {
      ...selectedItineraryEntityElement.value.texts,
    },
    title: `${currentDateLocalized(currentLanguage.value)}`,
  }).then((response) => {
    // notify user
    notify({
      type: 'success',
      title: 'Created',
      text: `New version ${currentUser.name} - ${currentDateLocalized(currentLanguage.value)} was created and saved!`,
    }, 4000);
    // updated elements array with new element
    selectedItineraryEntityOriginalContentVersions.value.push({
      ...response.data,
      icon: UserIconSolid,
      title: `${currentDateLocalized(currentLanguage.value)}`,
      titleAlternative: `${currentUser.name} - ${currentDateLocalized(currentLanguage.value)}`,
      start: selectedItineraryEntityElement.value.start,
      end: selectedItineraryEntityElement.value.end,
      type: selectedItineraryEntity.value.entity.type,
      parent_type: 'App\\Models\\PlaceEntity',
      parent_id: selectedItineraryEntity.value.entity.place_entity_id,
      destination: selectedItineraryEntity.value.entity.destination
    });
    // updated selected element
    selectedItineraryEntityElement.value = selectedItineraryEntityOriginalContentVersions.value[selectedItineraryEntityOriginalContentVersions.value.length - 1];
  }).catch((error) => {
    // notify user of error
    notify({
      type: 'danger',
      title: 'Something went wrong',
      text: `Could not create new version ${currentUser.name} - ${currentDateLocalized(currentLanguage.value)}`,
    }, 4000);
    // log error
    console.error('save error: ', error);
  }).finally(() => {
    // rest in peace
  });
}

/**
 * Overwrite itinerary entity content version to original entity.
 */
function overwriteOriginalEntityVersionWithProposalVersion() {
  axios.put(route('api.content-version.update', { contentVersion: selectedItineraryEntityElement.value.id }), {
    ...selectedItineraryEntityElement.value,
    data: proposalItineraryEntityElement.value.data,
    texts: proposalItineraryEntityElement.value.texts,
  }).then((response) => {
    // notify user
    notify({
      type: 'success',
      title: 'Saved',
      text: `Content for version ${selectedItineraryEntityElement.value.titleAlternative} saved`,
    }, 4000);
    // updated selected element with new data
    selectedItineraryEntityElement.value = {
      ...selectedItineraryEntityElement.value, ...response.data,
    };
    // updated elements array with new SelectedElement
    selectedItineraryEntityOriginalContentVersions.value[selectedItineraryEntityOriginalContentVersions.value.findIndex(
      (element) => element.id === selectedItineraryEntityElement.value.id,
    )] = {
      ...selectedItineraryEntityElement.value,
      data: proposalItineraryEntityElement.value.data,
      texts: proposalItineraryEntityElement.value.texts,
    };
  }).catch((error) => {
    // notify user of error
    notify({
      type: 'danger',
      title: 'Something went wrong',
      text: `Could not save content for version ${selectedItineraryEntityElement.value.titleAlternative}`,
    }, 4000);
    // log error
    console.error('save error: ', error);
  }).finally(() => {
    // update store with changed element
    store.fetchProposal(proposal.value.id);
  });
}

/**
 * Remove itinerary entity from proposal.
 */
function removeItineraryItem() {
  axios.delete(route('api.proposal.itinerary.delete', {
    proposal: proposal.value.id,
    itinerary: selectedItineraryEntity.value.entity.id,
  })).then(() => {
    notify({
      type: 'success',
      title: 'Removed',
      text: 'Entity removed from proposal',
    }, 4000);
    showItineraryEntitySettingsModal.value = false;
    selectedItineraryEntity.value = null;
    store.fetchEntities();
  })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.log(error);
    })
    .finally(() => {
      //
    });
}

/**
 * Toggles the visibility of the program section.
 * @param {object} listItem - The itinerary entity to toggle
 * @param {boolean} enabledState - The state to toggle to
 * @returns {void}
 */
function toggleItineraryItemVisibility(listItem, enabledState) {
  listItem.visibleInProgram = enabledState;
  entities.value[listItem.entityType][listItem.entityIndex].data.visibleInProgram = enabledState;

  // wait for user to stop toggling before saving to database
  store.setSaveTimer(
    () => {
      editEntityInProposal(listItem);
    },
    1500,
  );
}

/* *********************************************************************
 * Page lifecycle functions
 * ****************************************************************** */

onMounted(() => {
  // Temporary to have a default entity selected and modal open when developing
  // openItineraryEntitySettingsModal(itinerary.value[0]?.list[0] ?? itinerary.value[1]?.list[0] ?? null);
  // Temporary to have the map modal open when developing
  // showMapModal.value = true;
});

watch(openEntityItinerarySettings, () => {
  if (openEntityItinerarySettings.value) {
    const listItem = itinerary.value.flatMap((day) => day.list).find((item) => item.entity.id === openEntityItinerarySettings.value);
    openItineraryEntitySettingsModal(listItem);
    store.resetOpenEntityItinerarySettings();
  }
});

</script>

<style lang="scss">
.dp__week_num {
  font-size: small;
  color: #7eaae3;
}
</style>
